import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EvidenceGallery from './components/EvidenceGallery';
import SessionActiveQR from './components/SessionActiveQR';
import SessionWaitingPhotos from './components/SessionWaitingPhotos';
import { useInspectionSessionEvidence, EVIDENCE_PHASE } from 'hooks/useInspectionSessionEvidence';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import { IToolInspectionImageReq } from 'types/suscription/toolInspection.types';
import { SESSION_TIMEOUT_SECONDS } from 'constants/device-sync.constants';
import { createCountdownTick, mapSessionCompleteResToImagesReq } from '../../../utils/toolInspection.utils';

import './PhotoEvidenceSection.scss';

interface IPhotoEvidenceSectionProps {
  productId: number;
  interactionDisabled: boolean;
  images: IToolInspectionImageReq[] | null;
  setFormImages: Dispatch<SetStateAction<IToolInspectionImageReq[]>>;
}

const PhotoEvidenceSection = ({
  productId,
  interactionDisabled,
  images,
  setFormImages
}: IPhotoEvidenceSectionProps) => {
  const [isSessionConnectionRequested, setIsSessionConnectionRequested] = useState(false);
  const [timer, setTimer] = useState(SESSION_TIMEOUT_SECONDS);

  const {
    evidenceUrlForQr,
    uiPhase,
    sseCompletedEvidence,
    isStreamReady,
    streamError,
    confirmTransferredPhotos,
    rescanQr,
    changeUploadedPhotos
  } = useInspectionSessionEvidence({
    images,
    productId,
    enabled: isSessionConnectionRequested
  });

  const isSessionActive = isSessionConnectionRequested && isStreamReady;
  const isStreamConnecting = isSessionConnectionRequested && !isStreamReady && !streamError;

  const isQrPhase = uiPhase === EVIDENCE_PHASE.Qr;
  const isPhoneActivePhase = uiPhase === EVIDENCE_PHASE.PhoneActive;
  const isGalleryPhase = uiPhase === EVIDENCE_PHASE.Gallery;

  const showQr = isQrPhase && isSessionConnectionRequested;
  const showGallery = isGalleryPhase || (isQrPhase && !isSessionConnectionRequested);

  const displayedPhotos = useMemo((): IToolInspectionImageReq[] => {
    const transferredPhotos = sseCompletedEvidence?.payload?.photos;
    if (sseCompletedEvidence && transferredPhotos?.length) {
      return mapSessionCompleteResToImagesReq(sseCompletedEvidence);
    }
    return images || [];
  }, [sseCompletedEvidence, images]);

  const handleStartSession = () => {
    if (streamError) {
      rescanQr();
    }

    setIsSessionConnectionRequested(true);
    setTimer(SESSION_TIMEOUT_SECONDS);
  };

  const previousInteractionDisabledRef = useRef(interactionDisabled);

  const resetEvidenceSessionForReadOnly = useCallback(() => {
    rescanQr();
    setIsSessionConnectionRequested(false);
    setTimer(SESSION_TIMEOUT_SECONDS);
  }, [rescanQr]);

  const applyInteractionDisabledTransition = useCallback(() => {
    const wasInteractionDisabled = previousInteractionDisabledRef.current;
    if (!wasInteractionDisabled && interactionDisabled) {
      resetEvidenceSessionForReadOnly();
    }
    previousInteractionDisabledRef.current = interactionDisabled;
  }, [interactionDisabled, resetEvidenceSessionForReadOnly]);

  const handleResetSession = useCallback(() => {
    rescanQr();
    setTimer(SESSION_TIMEOUT_SECONDS);
  }, [rescanQr]);

  const handleChangePhotos = () => {
    changeUploadedPhotos();
    setIsSessionConnectionRequested(true);
    setTimer(SESSION_TIMEOUT_SECONDS);
  };

  const handleConfirmTransferredPhotos = useCallback(() => {
    if (sseCompletedEvidence == null) {
      displayErrorNotification({
        customJSXMessage: <>Primero debes subir las imágenes desde tu teléfono</>
      });
      return;
    }
    setFormImages(mapSessionCompleteResToImagesReq(sseCompletedEvidence));
    confirmTransferredPhotos();
  }, [sseCompletedEvidence, setFormImages, confirmTransferredPhotos]);

  const handleThumbnailClick = useCallback((photo: IToolInspectionImageReq) => {
    const url = photo.url?.trim();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const getQrPhaseCountdownCleanup = useCallback(() => {
    return createCountdownTick({
      enabled: isSessionActive && uiPhase === EVIDENCE_PHASE.Qr,
      setRemainingSeconds: setTimer,
      onExpired: handleResetSession,
      resetToSeconds: SESSION_TIMEOUT_SECONDS
    });
  }, [isSessionActive, uiPhase, handleResetSession]);

  useEffect(() => {
    applyInteractionDisabledTransition();
  }, [applyInteractionDisabledTransition]);

  useEffect(() => {
    return getQrPhaseCountdownCleanup();
  }, [getQrPhaseCountdownCleanup]);

  return (
    <div className="inspectionEvidenceSectionRoot">
      {showQr && (
        <SessionActiveQR
          qrUrl={evidenceUrlForQr}
          timeLeft={timer}
          isDisabled={interactionDisabled}
          onRegenerateQr={handleResetSession}
        />
      )}

      {isPhoneActivePhase && (
        <SessionWaitingPhotos
          onRescan={handleResetSession}
          onConfirm={handleConfirmTransferredPhotos}
          isDisabled={interactionDisabled}
        />
      )}

      {showGallery && (
        <EvidenceGallery
          photos={displayedPhotos}
          onAddPhotos={handleStartSession}
          onChangePhotos={handleChangePhotos}
          onThumbnailClick={handleThumbnailClick}
          isDisabled={interactionDisabled}
          isAddFlowBusy={isStreamConnecting}
        />
      )}

      {streamError && (
        <p className="inspectionEvidenceSectionStreamError" role="alert">
          {streamError}
        </p>
      )}
    </div>
  );
};

export default PhotoEvidenceSection;
